import { extractDates, extractUncertainDates, formatCents, statedAmounts } from "./figures";
import type { LeadCard } from "./leadCard";
import { windowState, DAY_MS, HOURS, STANDARD_WINDOW_MS, type WindowState } from "./window";
import { isWindowGated, type Channel, type EvidenceKind } from "./types";

/**
 * The owner's call-prep brief (L11, `pnpm drafts brief <thread>`).
 *
 * Agent Brains section 5.4 gives Concierge "call-prep (owner brief per consult)
 * ... measured on show rate", the canvas says "drafts and briefs, Mike sends",
 * and Research's rule is that "other agents consume the card, never raw
 * research". The owner works from a terminal while the inbox is not hosted
 * (decision list items 26, 65 and 107), so the brief is plain text.
 *
 * A PURE READ OF THE RECORD. `buildCallBrief` takes what the database already
 * holds about one thread (its lead card, its routing decision, its drafts and
 * outcomes, its window stamps) and arranges it. No model is called and nothing
 * is written. It states no inference as a fact (a lead card keeps facts,
 * inferences and held contradictions apart, and so does this), resolves no
 * contradiction (a held row is shown as held and never read from again, below),
 * and invents no talking point.
 *
 * WHAT TO BRING UP IS DERIVED, NEVER COMPOSED. Four kinds, each a rule over a
 * record, each line citing that record's id so the owner can check it:
 *   - a date: a FACT on the card whose text states a calendar date
 *     (`extractDates`, F-16's module, a month and a day, or a numeric date with
 *     its year);
 *   - a stated budget: a FACT whose evidence kind is `dm.stated-budget`, with
 *     the amounts its text states (`statedAmounts`, F-16's module). An amount
 *     the module cannot read whole ($8k, $3-4k) is never printed as a number:
 *     the line says so and sends the owner to the fact. Money in any other fact
 *     is not a budget and is not brought up (precision beats recall, section 0);
 *   - a message the agent handed to the owner: a gap ticket (a blocked or
 *     pending draft with `needs_owner_knowledge`, A3.6, section 13.4), named by
 *     the time of the customer message it is about, never by that message's
 *     words. The flag also marks complaint holding lines and blocked runs, so
 *     the wording claims only what the record says;
 *   - a follow-up that may be owed: a draft with the same flag that was
 *     released (`sent` or `transmitted`) and has had nothing from the shop or
 *     the agent after it.
 * An inference is never brought up: raising it states it as a fact. A held row
 * is two sources disagreeing, and picking one to raise is resolving it, which
 * only a human does (section 4). The schema links a held row to nothing, so a
 * held row HOLDS a topic by what it says it is: a budget topic when the row is
 * a stated budget or states money, a date topic when the row has the dated
 * fact's evidence kind or names a date. A held topic is not raised; one line
 * says it is held and cites the fact and the held rows. This errs toward
 * silence (section 0): a held row of the same kind but another subject also
 * silences a fact, and the fact stays on the card under WHO.
 *
 * HOLDS. The thread's own holds are read the way the send path reads them
 * (`DRAFT_HOLDS_COLUMNS`: an injection freeze nobody cleared, a pending deletion
 * request) and printed under WHERE IT STANDS. A freeze changes nothing here (the
 * brief prepares a call, it sends nothing). A pending erasure (A6.3) withholds
 * every record's words whatever the caller's gate says and raises nothing: the
 * owner is told why and given the request's code.
 *
 * WHAT IS PRINTED IS KEPT (decision list items 13, 14 and 15). On AWS the
 * command runs as a one-off task and every line lands in the migrate log group.
 * So no record's free text reaches a line except through `gate`, which the
 * caller builds from the contact record (the scrub in packages/db
 * operatorText.ts): it returns the text safe to print, or null when it cannot
 * be, and the line then says so and still cites the record. The detection
 * above runs on the raw text, so a withheld fact is still found, and the
 * owner is told to read it where it is kept. `sourceQuote` is a customer's
 * verbatim words, the stored-and-replayed surface of section 6, and is never
 * read here.
 *
 * WINDOW AND CLOCK. The window state is core's `windowState` (the T3 machine,
 * read and never restated) on the thread's own last customer stamp, the value
 * the send path reads. Grace grants nothing (F-01). Off Meta's conversation
 * rails the window is not applicable, said out loud (F-20). Every age is
 * floored, so an age is never overstated, from the one `now` the caller passes.
 *
 * SEAM FOR L6. `leadQuality` is where a lead-quality stage's lines print,
 * under WHO, each citing a record. This file imports nothing from that stage.
 * U3 QUALIFY feeds it one line, the contact's latest why-now sentence citing
 * its `lead.scored` event (`loadLatestWhyNow`, wired in draftsCli.ts); never a
 * band or a score. The seam stays a shape with an empty default.
 */

/** What a caller's text gate returns for text it cannot print here. */
const WITHHELD = "(text withheld)";

/** Text safe to print, or null when it is not (see the module comment). */
export type BriefText = (raw: string) => string | null;

export interface CallBriefThread {
  readonly threadId: string;
  readonly channel: Channel;
  /** The contact's pseudonym, the only name this brief uses for a person. */
  readonly contactToken: string;
  readonly contactId: string;
  /** The stamp the window and the send gate read. Null: no customer message. */
  readonly lastCustomerMessageAt: Date | null;
  /** The newest message the shop or the agent sent on the thread. */
  readonly lastShopMessage: { readonly at: Date; readonly sender: "shop" | "agent" } | null;
  /**
   * Where a phone or email was found, named and never valued: the contact record,
   * the customer's own messages, contact memory (`contactAskSkip`'s reading, the
   * one `show` and `approve` use). Empty: nowhere. There is no consent column (F-20).
   */
  readonly contactCaptured: readonly ContactSource[];
}

export type ContactSource = "contact_record" | "thread" | "contact_memory";

/** What holds this thread right now, the measure `DRAFT_HOLDS_COLUMNS` reads. */
export interface CallBriefHolds {
  /** The first detection of an injection freeze nobody has cleared. Null: not frozen. */
  readonly frozenSince: Date | null;
  /** Codes of the contact's deletion requests that are still pending (A6.3). */
  readonly erasurePending: readonly string[];
}

/**
 * The thread's latest `routing.decided` event. A null scenario or map version
 * is a row this build cannot vouch for (the caller checks both against what it
 * knows before they reach here): the brief says a decision exists and prints
 * none of it.
 */
export interface CallBriefRoute {
  readonly scenario: string | null;
  readonly mapVersion: string | null;
  readonly decidedAt: Date;
}

/**
 * What the transmit task did with a `sent` draft (M2 BRIEFREFUSED, decision row 229(b)). `sent` means the gates
 * passed, not that anything left: a refused transmit leaves the draft in `sent` with a withheld ledger row.
 * Ids, a gate enum and a time only, never a word of the draft or of the ledger row.
 */
export type CallBriefTransmit =
  | {
      readonly kind: "withheld";
      /** The gate enum; null when the ledger row's gate is not a plain enum word. */
      readonly gate: string | null;
      /** Null only with `unrecorded`: the claim says it never sent, but no refusal row is on record. */
      readonly at: Date | null;
      readonly unrecorded?: boolean;
    }
  | { readonly kind: "uncertain" }
  | { readonly kind: "pending" };

/** A draft on the thread that is blocked, waiting for approval, or released with the owner flag. */
export interface CallBriefDraft {
  readonly id: string;
  readonly status: string;
  readonly createdAt: Date;
  readonly needsOwnerKnowledge: boolean;
  /** The drafts CLI's own flags for it (REPAIRED, NEEDS YOU, and so on), already words. */
  readonly flags: readonly string[];
  /** When the customer message it answers was sent. Null: no reply run names one. */
  readonly answeredAt: Date | null;
  /** When it was released: `transmitted_at`, else `sent_at`. Null: not released, or not stamped. */
  readonly releasedAt: Date | null;
  /**
   * A `sent` draft's transmit outcome. Absent or null: print as released (a transmitted draft, or one whose
   * transmit task has a claim on record).
   */
  readonly transmit?: CallBriefTransmit | null;
}

export interface CallBriefOutcome {
  readonly id: string;
  readonly label: string;
  readonly amountCents: number | null;
  readonly source: string;
  readonly recordedAt: Date;
  /** The record was withdrawn: `reversed_at` is set, or an `outcome.reversed` event stands (item 145). */
  readonly withdrawn: boolean;
}

/** One line a lead-quality stage may give the owner, with the record it rests on. */
export interface CallBriefLeadQualityLine {
  readonly text: string;
  readonly cites: string;
}

export interface CallBriefInput {
  readonly now: Date;
  readonly thread: CallBriefThread;
  readonly card: LeadCard;
  readonly route: CallBriefRoute | null;
  readonly holds: CallBriefHolds;
  readonly drafts: readonly CallBriefDraft[];
  readonly outcomes: readonly CallBriefOutcome[];
  /** SEAM (L6): see the module comment. Absent or null prints nothing. */
  readonly leadQuality?: readonly CallBriefLeadQualityLine[] | null;
}

export type CallBriefWindow =
  | { readonly applies: false; readonly channel: Channel }
  | {
      readonly applies: true;
      readonly state: WindowState;
      /** Ms since the customer's last message. Null: none. Negative: stamped ahead of this clock. */
      readonly sinceMs: number | null;
      /** Ms until the window closes, floored at the caller. Null when closed or not known. */
      readonly leftMs: number | null;
    };

export type CallBriefBringUp =
  | {
      readonly kind: "dated_fact";
      readonly cites: string;
      readonly evidenceKind: EvidenceKind;
      /**
       * Verbatim, raw: gated at render. Empty when the text states any date
       * whose month is a "may" not written "May" (item 153), alone or beside a
       * readable one: then `readable` is false and no date is printed, so part
       * of the fact never reads as all of it.
       */
      readonly dates: readonly string[];
      readonly readable: boolean;
    }
  | {
      readonly kind: "stated_budget";
      readonly cites: string;
      readonly evidenceKind: EvidenceKind;
      /**
       * Verbatim, raw: gated at render. Empty when the text states no amount, or
       * any amount the figures module cannot read whole (then `readable` is false
       * and no amount is printed, so a truncated figure never reaches the owner).
       */
      readonly amounts: readonly string[];
      readonly readable: boolean;
    }
  | {
      readonly kind: "held_not_raised";
      readonly topic: "date" | "budget";
      /** The fact first, then the held rows that hold its topic. */
      readonly cites: readonly string[];
    }
  | {
      readonly kind: "handed_to_owner";
      readonly cites: string;
      readonly status: string;
      readonly answeredAt: Date | null;
    }
  | {
      readonly kind: "follow_up_owed";
      readonly cites: string;
      readonly status: string;
      readonly releasedAt: Date;
      readonly transmit: CallBriefTransmit | null;
    };

export interface CallBrief {
  readonly now: Date;
  readonly thread: CallBriefThread;
  readonly card: LeadCard;
  readonly route: CallBriefRoute | null;
  readonly holds: CallBriefHolds;
  readonly window: CallBriefWindow;
  /** Blocked or pending drafts that need the owner's knowledge (A3.6), newest first. */
  readonly gapTickets: readonly CallBriefDraft[];
  /** Released drafts with the owner flag and nothing from the shop or agent after them, newest first. */
  readonly followUps: readonly CallBriefDraft[];
  /** Drafts waiting for approval, newest first. */
  readonly pending: readonly CallBriefDraft[];
  /** Outcomes still standing, oldest first. */
  readonly outcomes: readonly CallBriefOutcome[];
  readonly withdrawnOutcomes: number;
  readonly bringUp: readonly CallBriefBringUp[];
  readonly leadQuality: readonly CallBriefLeadQualityLine[];
}

const MINUTE_MS = 60_000;

const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? "" : "s"}`;

/**
 * A span in words, floored so it is never overstated: minutes, then hours up
 * to two days, then days. The drafts CLI's own reading of an age (it imports
 * this), so a brief and a `pnpm drafts show` never disagree about one.
 */
export function elapsedWords(ms: number): string {
  if (ms < MINUTE_MS) return "under a minute";
  if (ms < HOURS) return plural(Math.floor(ms / MINUTE_MS), "minute");
  if (ms < 2 * DAY_MS) return plural(Math.floor(ms / HOURS), "hour");
  return plural(Math.floor(ms / DAY_MS), "day");
}

function windowOf(thread: CallBriefThread, now: Date): CallBriefWindow {
  if (!isWindowGated(thread.channel)) return { applies: false, channel: thread.channel };
  const last = thread.lastCustomerMessageAt;
  const state = windowState({ lastCustomerMessageAt: last, now });
  if (last === null) return { applies: true, state, sinceMs: null, leftMs: null };
  const sinceMs = now.getTime() - last.getTime();
  const leftMs = state === "closed" || sinceMs < 0 ? null : STANDARD_WINDOW_MS - sinceMs;
  return { applies: true, state, sinceMs, leftMs };
}

const newestFirst = (a: CallBriefDraft, b: CallBriefDraft): number =>
  b.createdAt.getTime() - a.createdAt.getTime() || b.id.localeCompare(a.id);

/** The brief's structure, from the record. Pure: the same input gives the same brief. */
export function buildCallBrief(input: CallBriefInput): CallBrief {
  const drafts = [...input.drafts].sort(newestFirst);
  const gapTickets = drafts.filter(
    (d) => d.needsOwnerKnowledge && (d.status === "blocked" || d.status === "pending_approval"),
  );
  const shopAt = input.thread.lastShopMessage?.at.getTime() ?? null;
  // The release's own message is stamped at the release (transmit.ts), so only a
  // message strictly after it is a follow-up.
  const followUps = drafts.filter(
    (d) =>
      d.needsOwnerKnowledge &&
      (d.status === "sent" || d.status === "transmitted") &&
      d.releasedAt !== null &&
      (shopAt === null || shopAt <= d.releasedAt.getTime()),
  );
  const pending = drafts.filter((d) => d.status === "pending_approval");
  const outcomes = input.outcomes
    .filter((o) => !o.withdrawn)
    .sort((a, b) => a.recordedAt.getTime() - b.recordedAt.getTime() || a.id.localeCompare(b.id));

  // A pending erasure raises nothing (see the module comment).
  const erased = input.holds.erasurePending.length > 0;
  const held = input.card.heldContradictions;
  const statesMoney = (text: string): boolean => {
    const m = statedAmounts(text);
    return m.amounts.length + m.unreadable.length > 0;
  };
  const budgetHolders = held.filter((h) => h.evidenceKind === "dm.stated-budget" || statesMoney(h.content));
  const holdsOf = (holders: readonly { readonly memoryId: string }[], fact: string): readonly string[] => [
    fact,
    ...holders.map((h) => h.memoryId),
  ];

  const bringUp: CallBriefBringUp[] = [];
  if (!erased) {
    for (const f of input.card.facts) {
      const dates = extractDates(f.content);
      // Item 153, third review: a may not written "May" is still a date here,
      // so the held-row check below sees a fact whose only date is one (base
      // read it as a date), and such a fact is never silently dropped.
      const unreadable = extractUncertainDates(f.content).length > 0;
      if (dates.length === 0 && !unreadable) continue;
      // Item 153: a held row whose date has a "may" not written "May" is not a
      // date to raise, and it is still a date that holds one (rec accepted 2026-10-02).
      const holders = held.filter(
        (h) =>
          h.evidenceKind === f.evidenceKind ||
          extractDates(h.content).length > 0 ||
          extractUncertainDates(h.content).length > 0,
      );
      if (holders.length > 0) {
        bringUp.push({ kind: "held_not_raised", topic: "date", cites: holdsOf(holders, f.memoryId) });
        continue;
      }
      // Item 153, second review: "June 3 or may 14" is not "June 3". A fact
      // that also states a date this brief cannot read prints no date at all,
      // as a budget it cannot read whole prints no amount. Third review: the
      // same when that is its only date ("may 14"), never nothing.
      const readable = !unreadable;
      bringUp.push({ kind: "dated_fact", cites: f.memoryId, evidenceKind: f.evidenceKind, dates: readable ? dates : [], readable });
    }
    for (const f of input.card.facts) {
      if (f.evidenceKind !== "dm.stated-budget") continue;
      if (budgetHolders.length > 0) {
        bringUp.push({ kind: "held_not_raised", topic: "budget", cites: holdsOf(budgetHolders, f.memoryId) });
        continue;
      }
      const m = statedAmounts(f.content);
      const readable = m.amounts.length > 0 && m.unreadable.length === 0;
      bringUp.push({
        kind: "stated_budget",
        cites: f.memoryId,
        evidenceKind: f.evidenceKind,
        amounts: readable ? m.amounts : [],
        readable,
      });
    }
    for (const d of gapTickets) {
      bringUp.push({ kind: "handed_to_owner", cites: d.id, status: d.status, answeredAt: d.answeredAt });
    }
    for (const d of followUps) {
      bringUp.push({
        kind: "follow_up_owed",
        cites: d.id,
        status: d.status,
        releasedAt: d.releasedAt!,
        transmit: d.status === "sent" ? (d.transmit ?? null) : null,
      });
    }
  }

  return {
    now: input.now,
    thread: input.thread,
    card: input.card,
    route: input.route,
    holds: input.holds,
    window: windowOf(input.thread, input.now),
    gapTickets,
    followUps,
    pending,
    outcomes,
    withdrawnOutcomes: input.outcomes.filter((o) => o.withdrawn).length,
    bringUp,
    leadQuality: input.leadQuality ?? [],
  };
}

const LABEL_WIDTH = 14;
const field = (label: string, value: string): string => `    ${label.padEnd(LABEL_WIDTH)}${value}`;
const iso = (d: Date): string => d.toISOString();

/** "3 hours ago", or why no age is stated. */
const agoWords = (now: Date, at: Date): string => {
  const ms = now.getTime() - at.getTime();
  return ms < 0 ? "stamped ahead of this clock" : `${elapsedWords(ms)} ago`;
};

/** "3 hours ago", or that the stamp is ahead of this clock. */
const releasedWords = (now: Date, at: Date): string => {
  const ms = now.getTime() - at.getTime();
  return ms < 0 ? "ahead of this clock" : `${elapsedWords(ms)} ago`;
};

/**
 * What a follow-up draft's release is said as. A `sent` draft that the transmit task refused is withheld, one it
 * has not reached yet is pending, and anything else is released as it always was.
 */
const transmitWords = (
  now: Date,
  d: { readonly status: string; readonly releasedAt: Date; readonly transmit?: CallBriefTransmit | null },
): { readonly short: string; readonly kind: "withheld" | "uncertain" | "pending" | "released" } => {
  const t = d.status === "sent" ? d.transmit : null;
  if (t?.kind === "withheld") {
    return {
      short:
        t.unrecorded === true || t.at === null
          ? "withheld (gate unknown), answer by hand"
          : `withheld ${t.gate === null ? "(gate not readable)" : t.gate} at ${t.at.toISOString()}, answer by hand`,
      kind: "withheld",
    };
  }
  // The provider's answer was lost: the message may be on the customer's screen, so this is not a release.
  if (t?.kind === "uncertain") {
    return { short: "transmit uncertain, may have reached the customer, check the Meta inbox", kind: "uncertain" };
  }
  if (t?.kind === "pending") return { short: "transmit pending", kind: "pending" };
  return { short: `released ${releasedWords(now, d.releasedAt)}`, kind: "released" };
};

/** How each place a contact was found is said, in a fixed order whatever order the caller found them in. */
const CONTACT_SOURCES: readonly (readonly [ContactSource, string])[] = [
  ["contact_record", "the contact record holds a phone or email"],
  ["thread", "the customer's messages hold a number or email"],
  ["contact_memory", "contact memory holds one"],
];

function windowWords(w: CallBriefWindow): string {
  if (!w.applies) {
    return `not applicable (${w.channel}): the 24-hour window governs Instagram and Facebook messages`;
  }
  if (w.sinceMs === null) return "closed, no customer message is on record";
  if (w.state === "closed") {
    return `closed, the customer wrote ${elapsedWords(w.sinceMs)} ago, past 24 hours, so a free-form reply is refused`;
  }
  if (w.leftMs === null) return "in window, the customer's last message is stamped ahead of this clock";
  if (w.state === "grace") {
    return `grace, closes in ${elapsedWords(w.leftMs)}, and grace grants nothing extra (F-01)`;
  }
  return `in window, closes in ${elapsedWords(w.leftMs)}`;
}

/**
 * The brief as lines: WHO, WHERE IT STANDS, WHAT IS OPEN, WHAT TO BRING UP.
 * The caller prints a header above them and the thread's last message below,
 * because that display, and its bound, belong to the drafts CLI.
 */
export function renderCallBrief(brief: CallBrief, callerGate: BriefText): string[] {
  let withheld = false;
  const erasure = brief.holds.erasurePending;
  const erased = erasure.length > 0;
  const ids = (cites: readonly string[]): string => cites.map((c) => `[${c}]`).join(" ");
  /** The caller's gate, shut for good while an erasure is pending (A6.3): no record's words print. */
  const gate: BriefText = (raw) => (erased ? null : callerGate(raw));
  /** `| text`, or the withheld marker. For a record's own words. */
  const quoted = (raw: string): string => {
    const g = gate(raw);
    if (g === null) {
      withheld = true;
      return WITHHELD;
    }
    return `| ${g}`;
  };
  /** Text inside a sentence, or the withheld marker when any piece of it is. */
  const inline = (raws: readonly string[]): string => {
    const g = raws.map(gate);
    if (g.some((x) => x === null)) {
      withheld = true;
      return WITHHELD;
    }
    return g.join(", ");
  };
  const { now, thread, card } = brief;

  const who: string[] = ["  WHO", field("contact", `${thread.contactToken} (${thread.contactId}) on ${thread.channel}`)];
  const rows = (
    label: string,
    n: number,
    each: (i: number) => string[],
  ): void => {
    who.push(field(label, n === 0 ? "none" : String(n)));
    for (let i = 0; i < n; i++) who.push(...each(i));
  };
  rows("facts", card.facts.length, (i) => {
    const f = card.facts[i]!;
    return [
      `      ${"FACT".padEnd(LABEL_WIDTH - 2)}${quoted(f.content)}`,
      `      ${" ".repeat(LABEL_WIDTH - 2)}${f.evidenceKind}, written ${f.writtenAt.slice(0, 10)}, ${f.memoryId}`,
    ];
  });
  rows("inferences", card.inferences.length, (i) => {
    const f = card.inferences[i]!;
    return [
      `      INFERENCE (confidence ${f.confidence.toFixed(2)}, not a fact)  ${quoted(f.content)}`,
      `      ${" ".repeat(LABEL_WIDTH - 2)}${f.evidenceKind}, written ${f.writtenAt.slice(0, 10)}, ${f.memoryId}`,
    ];
  });
  rows("held", card.heldContradictions.length, (i) => {
    const h = card.heldContradictions[i]!;
    return [
      `      HELD, not resolved  ${quoted(h.content)}`,
      `      ${" ".repeat(LABEL_WIDTH - 2)}${h.evidenceKind}${h.isInference ? " (held as an inference)" : ""}, ` +
        `written ${h.writtenAt.slice(0, 10)}, ${h.memoryId}`,
      `      ${" ".repeat(LABEL_WIDTH - 2)}the save-gate's reason  ${quoted(h.saveReason)}`,
    ];
  });
  if (brief.leadQuality.length > 0) {
    brief.leadQuality.forEach((line, i) => {
      who.push(field(i === 0 ? "lead quality" : "", `${quoted(line.text)} [${line.cites}]`));
    });
  }
  const notShown = [
    card.excludedMalformed > 0 ? `${plural(card.excludedMalformed, "row")} the card could not read` : null,
    card.excludedRetired > 0 ? plural(card.excludedRetired, "retired row") : null,
    // M1: a row the recall screen quarantined is on no list a reader acts on; the count says it exists.
    card.quarantined.length > 0 ? `${plural(card.quarantined.length, "row")} the recall screen quarantined` : null,
  ].filter((p): p is string => p !== null);
  if (notShown.length > 0) who.push(field("not shown", notShown.join(", ")));

  const stand: string[] = ["  WHERE IT STANDS"];
  stand.push(
    field(
      "routed",
      brief.route === null
        ? "none"
        : brief.route.scenario === null || brief.route.mapVersion === null
          ? `unreadable: a decision was recorded ${agoWords(now, brief.route.decidedAt)}`
          : `${brief.route.scenario} (${brief.route.mapVersion}), decided ${agoWords(now, brief.route.decidedAt)}`,
    ),
  );
  stand.push(field("window", windowWords(brief.window)));
  const last = thread.lastCustomerMessageAt;
  stand.push(field("customer last", last === null ? "none" : `${agoWords(now, last)} (${iso(last)})`));
  const shop = thread.lastShopMessage;
  stand.push(
    field("shop last", shop === null ? "none" : `${agoWords(now, shop.at)} (${iso(shop.at)}), sent by ${shop.sender}`),
  );
  const found = CONTACT_SOURCES.filter(([source]) => thread.contactCaptured.includes(source)).map(([, words]) => words);
  stand.push(
    field(
      "contact info",
      found.length > 0
        ? `captured: ${found.join("; ")}`
        : "not captured: no phone or email in the contact record, the customer's messages or contact memory",
    ),
  );
  const holdLines: string[] = [];
  if (brief.holds.frozenSince !== null) {
    holdLines.push(
      `frozen since ${iso(brief.holds.frozenSince)}: an injection freeze (section 12), no draft on this thread is released until a person clears it`,
    );
  }
  if (erased) {
    holdLines.push(
      `erasure pending (A6.3): a deletion request for this contact is open, so no record's words print here and nothing is raised ${ids(erasure)}`,
    );
  }
  if (holdLines.length === 0) stand.push(field("holds", "none"));
  holdLines.forEach((line, i) => stand.push(field(i === 0 ? "holds" : "", line)));

  const age = (d: CallBriefDraft): string => {
    const ms = now.getTime() - d.createdAt.getTime();
    return ms < 0 ? "created ahead of this clock" : `${elapsedWords(ms)} old`;
  };
  const open: string[] = ["  WHAT IS OPEN"];
  open.push(field("gap tickets", brief.gapTickets.length === 0 ? "none" : String(brief.gapTickets.length)));
  for (const d of brief.gapTickets) {
    open.push(
      `      ${d.id}  ${d.status === "pending_approval" ? "pending approval" : d.status}, ${age(d)}, ` +
        "flagged needs_owner_knowledge (A3.6)",
    );
  }
  open.push(field("follow-ups", brief.followUps.length === 0 ? "none" : String(brief.followUps.length)));
  for (const d of brief.followUps) {
    open.push(
      `      ${d.id}  ${d.status}, ${transmitWords(now, { ...d, releasedAt: d.releasedAt! }).short}, flagged needs_owner_knowledge, ` +
        "nothing from the shop or the agent since",
    );
  }
  open.push(field("pending", brief.pending.length === 0 ? "none" : String(brief.pending.length)));
  for (const d of brief.pending) {
    open.push(
      `      ${d.id}  pending approval, ${age(d)}${d.flags.length > 0 ? `, ${d.flags.join(", ")}` : ""}` +
        `  (pnpm drafts show ${d.id})`,
    );
  }
  open.push(
    field(
      "outcomes",
      `${brief.outcomes.length === 0 ? "none" : String(brief.outcomes.length)}` +
        `${brief.withdrawnOutcomes > 0 ? `, ${brief.withdrawnOutcomes} withdrawn` : ""}`,
    ),
  );
  for (const o of brief.outcomes) {
    open.push(
      `      ${o.id}  ${o.label}  ${o.amountCents === null ? "no amount" : formatCents(o.amountCents)}  ` +
        `recorded ${iso(o.recordedAt)} (${o.source})`,
    );
  }

  const bring: string[] = [
    "  WHAT TO BRING UP",
    "    Only what the record states. No model wrote it, and it is not advice or a script.",
  ];
  if (erased) {
    bring.push(`    none, erasure pending (A6.3): nothing is raised while a deletion request is open ${ids(erasure)}`);
  } else if (brief.bringUp.length === 0) bring.push("    none");
  for (const b of brief.bringUp) {
    switch (b.kind) {
      case "dated_fact":
        bring.push(
          b.readable
            ? `    - A date the card holds: ${inline(b.dates)} (fact, ${b.evidenceKind}) [${b.cites}]`
            : `    - A date the card holds: a date this brief cannot read exactly, read the fact (${b.evidenceKind}) [${b.cites}]`,
        );
        break;
      case "stated_budget":
        bring.push(
          b.readable
            ? `    - A budget the customer stated: ${inline(b.amounts)} (fact, ${b.evidenceKind}) [${b.cites}]`
            : `    - A budget the customer stated: an amount this brief cannot read exactly, read the fact (${b.evidenceKind}) [${b.cites}]`,
        );
        break;
      case "held_not_raised":
        bring.push(
          `    - Held, not raised: a ${b.topic === "date" ? "date" : "stated budget"} on the card sits beside a held row, ` +
            `so it waits for a person to resolve it ${ids(b.cites)}`,
        );
        break;
      case "handed_to_owner":
        bring.push(
          `    - A message the agent handed to the owner: ${
            b.answeredAt === null
              ? "no reply run names the message it was about"
              : `the customer's message of ${iso(b.answeredAt)}`
          } (gap ticket, ${b.status}, flagged needs_owner_knowledge) [${b.cites}]`,
        );
        break;
      case "follow_up_owed":
        {
          // F-06: `sent` (gates passed) is not `transmitted` (left the building).
          // Only a transmitted draft "went out". A sent one the transmit task refused was never sent
          // to the customer (M2), and one it has not reached yet is waiting.
          const said = transmitWords(now, b);
          bring.push(
            `    - A follow-up may be owed: a draft flagged needs_owner_knowledge ${
              b.status === "transmitted"
                ? "went out"
                : said.kind === "withheld"
                  ? "was withheld, never sent to the customer"
                  : said.kind === "uncertain"
                    ? "may or may not have reached the customer"
                    : said.kind === "pending"
                    ? "is waiting to transmit"
                    : "was released"
            } (${b.status}, ${said.short}) and nothing from the shop or the agent has followed it [${b.cites}]`,
          );
        }
        break;
    }
  }

  if (withheld) {
    who.splice(
      2,
      0,
      ...(erased
        ? [
            field("withheld", "a deletion request for this contact is pending (A6.3), so no record's words print here."),
            field("", "Records are named by id, kind and date only."),
          ]
        : [
            field("withheld", "a record's words print only after the scrub (the contact record's identity becomes the"),
            field("", "pseudonym, the shared redactor takes the rest). Where that could not be done safely the text is"),
            field("", "withheld and the record id is given. A customer message is read in the Meta inbox. A memory row"),
            field("", "is kept only in contact_memory, and the app that would show it is not hosted yet (decision"),
            field("", "list items 26, 65 and 107)."),
          ]),
    );
  }
  return [...who, "", ...stand, "", ...open, "", ...bring];
}
